import React from 'react';

const LinesOverview = ({ game, sport, homeTeam, awayTeam }) => {
    // Format odds to add + sign for positive values
    const formatOdds = (odds) => {
        if (odds === null || odds === undefined) return 'N/A';
        return odds > 0 ? `+${odds}` : `${odds}`;
    };

    // Format spread/runline points to add + sign for positive values
    const formatPoints = (points) => {
        if (points === null || points === undefined) return 'N/A';
        return points > 0 ? `+${points}` : `${points}`;
    };

    // Get first book's most recent totals line
    const getTotalsLine = (sport) => {
        if (!game?.totals || !Array.isArray(game.totals) || game.totals.length === 0) {
            return null;
        }
        
        const firstBook = game.totals.find(book => book.lines && book.lines.length > 0);
        if (!firstBook) return null;
        
        const recentLine = firstBook.lines[0];
        
        return {
            book: firstBook.book,
            line: sport === 'mlb' ? recentLine.runs : recentLine.pts,
            over: recentLine.over,
            under: recentLine.under
        };
    };

    // Get first book's most recent moneyline
    const getMoneylineLine = () => {
        if (!game?.ml || !Array.isArray(game.ml) || game.ml.length === 0) {
            return null;
        }
        
        const firstBook = game.ml.find(book => book.lines && book.lines.length > 0);
        if (!firstBook) return null;
        
        const recentLine = firstBook.lines[0];
        
        return {
            book: firstBook.book,
            home: recentLine.home,
            away: recentLine.away
        };
    };

    // Get first book's most recent spread/runline
    const getSpreadLine = (sport) => {
        let spreadData = null;
        
        if (sport === 'mlb') {
            spreadData = game?.runline;
        } else {
            spreadData = game?.spread;
        }
        
        if (!spreadData || !Array.isArray(spreadData) || spreadData.length === 0) {
            return null;
        }
        
        const firstBook = spreadData.find(book => book.lines && book.lines.length > 0);
        if (!firstBook) return null;
        
        const recentLine = firstBook.lines[0];
        
        return {
            book: firstBook.book,
            type: sport === 'mlb' ? 'runline' : 'spread',
            home: {
                points: sport === 'mlb' ? recentLine.home.runs : recentLine.home.pts,
                odds: recentLine.home.odds
            },
            away: {
                points: sport === 'mlb' ? recentLine.away.runs : recentLine.away.pts,
                odds: recentLine.away.odds
            }
        };
    };

    const totalsLine = getTotalsLine(sport);
    const moneylineLine = getMoneylineLine();
    const spreadLine = getSpreadLine(sport);

    // If no lines are available, show unavailable message
    if (!totalsLine && !moneylineLine && !spreadLine) {
        return (
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Lines Overview
                </h3>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    No lines available
                </div>
            </div>
        );
    }

    return (
        <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Lines Overview
            </h3>
            
            <div className="space-y-3 flex flex-col justify-center items-center">
                {/* Moneyline */}
                {moneylineLine && (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Moneyline
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {moneylineLine.book}
                            </span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-12">{homeTeam}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOdds(moneylineLine.home)}
                                </span>
                            </div>
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-12">{awayTeam}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOdds(moneylineLine.away)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Spread/Runline */}
                {spreadLine && (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {spreadLine.type === 'runline' ? 'Runline' : 'Spread'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {spreadLine.book}
                            </span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-12">{homeTeam}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatPoints(spreadLine.home.points)} ({formatOdds(spreadLine.home.odds)})
                                </span>
                            </div>
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-12">{awayTeam}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatPoints(spreadLine.away.points)} ({formatOdds(spreadLine.away.odds)})
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Totals */}
                {totalsLine && (
                    <div className="w-full xl:w-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Totals
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {totalsLine.book}
                            </span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-16">Over {totalsLine.line}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOdds(totalsLine.over)}
                                </span>
                            </div>
                            <div className="flex justify-start gap-8 text-sm">
                                <span className="text-gray-600 dark:text-gray-300 w-16">Under {totalsLine.line}</span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                    {formatOdds(totalsLine.under)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LinesOverview;
